import { DatePipe } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormControl } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../../../environments/environment';
import { PAGE_NAMES } from '../../../core/helpers/constants';
import { IPermission } from '../../../core/models/auth.models';
import { ICampaign } from '../../../core/models/campaigns.model';
import { IReportingPeriod } from '../../../core/models/reporting.models';
import { AppService } from '../../../store/app.service';
import { ReportsApiService } from '../reports.apiservice';
import { InstanceService } from '../../../core/instance/instance.service';
import { getLocalReportingPeriodDates } from '../report-period-utils';

@Component({
  selector: 'app-reports-raw-campaigns-data',
  templateUrl: './reports-raw-campaigns-data.component.html',
  styleUrls: ['./reports-raw-campaigns-data.component.scss']
})
export class ReportsRawCampaignsDataComponent implements OnInit, OnDestroy {
  doWhat: IPermission = {} as IPermission;
  columns: Array<any> = [];
  campaignsData: Array<ICampaign>
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private appService: AppService,
    private fb: FormBuilder,
    private instance: InstanceService,
    private translate: TranslateService,
    private reportsService: ReportsApiService,) {
    this.campaignsData = new Array<ICampaign>();

    this.columns = [
      { title: 'NAME', field: 'name', sortable: true, searchable: true },
      { title: 'CLIENT', field: 'clientName', sortable: true, searchable: true },
      { title: 'PLATFORM', field: 'platform', sortable: true, pipe: 'P_CampaignPlatformTitle', searchable: true },
      { title: 'DMS', field: 'dmsName', sortable: true, searchable: true },
      { title: 'STAGE', field: 'stageId', sortable: true, pipe: 'P_CampaignStageTitle', searchable: true },
      { title: 'ACTIVE_SUBSCRIPTION', field: 'subscriptionActive', pipe: 'P_Boolean', searchable: true },
    ];
  }

  reportForm: FormGroup = this.fb.group({
    key: [null, [Validators.required]],
    startDate: [null, [Validators.required]],
    stopDate: [null, [Validators.required]],
  });
  get startDate() { return this.reportForm.get('startDate') as FormControl; }
  get stopDate() { return this.reportForm.get('stopDate') as FormControl; }

  reportPeriodOptions: Array<IReportingPeriodOption> = [];

  ngOnInit(): void {
    this.appService.setPageTitle$('RAW_CAMPAIGNS_DATA');
    this.doWhat = this.appService.getUserRolesForPage(PAGE_NAMES.Reports);

    this.translate.onLangChange
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => this.refreshReportPeriodTitles());
    this.translate.onTranslationChange
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => this.refreshReportPeriodTitles());

    this.appService.getAllCampaigns$()
      .pipe(takeUntil(this.destroyed$)
      )
      .subscribe((response: Array<ICampaign>) => {
        this.campaignsData = [...response];
        this.campaignsData = this.campaignsData.filter(f => f.platform == 'Google');
        this.campaignsData = this.campaignsData.filter(f => f.platformAccountId || f.platformCampaignId);
      });
    const savedSearchId = this.route.snapshot.queryParams['savedsearchid'];
    this.appService.fetchAllCampaigns({ SavedSearchId: savedSearchId });

    this.getSupportedReportingPeriods();
  }

  viewReportOnNewTab(evt: any) {
    this.validateFormAndOpen(evt, true);
  }

  reportformsubmitted = false;
  DateStart = '' as string | null;
  DateStop = '' as string | null;
  validateFormAndOpen(evt: any, isExternal: boolean): void {
    this.reportformsubmitted = true;
    if (this.reportForm.invalid)
      return;

    if (this.reportForm.touched == true) {
      this.DateStart = this.startDate.value;
      this.DateStop = this.stopDate.value;
    }

    let campaign = this.campaignsData.find(f => f.id == evt.id);

    let AccountID = campaign?.platformAccountId;
    let CampaignID = campaign?.platformCampaignId;
    let Platform = campaign?.platform;

    if (AccountID) AccountID = AccountID.replace(/\ /g, '').replace(/\-/g, '');

    let params = { accountId: AccountID, campaignId: CampaignID, platform: Platform, dateStart: this.DateStart, dateStop: this.DateStop };
    // console.log(this.DateStart, this.DateStop);
    // console.log(this.reportForm);
    if (AccountID || CampaignID) {
      if (isExternal) {
        const url = this.router.serializeUrl(
          this.router.createUrlTree(['/', 'dashboard', 'raw-campaigns-data'], { queryParams: params })
        );
        window.open(environment.apiBaseUrl + url, '_blank');
      }
      else {
        this.router.navigate(['/', 'dashboard', 'raw-campaigns-data'], { queryParams: params });
      }
    }
    else {
      Swal.fire(
        this.translate.instant('RAW_CAMPAIGNS_CANNOT_VIEW_REPORT_TITLE'),
        this.translate.instant('RAW_CAMPAIGNS_CANNOT_VIEW_REPORT_TEXT'),
        'error'
      )
    }
  }

  reportPeriods: Array<IReportingPeriod> = [];
  getSupportedReportingPeriods() {
    this.reportsService.getSupportedReportingPeriods()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: Array<IReportingPeriod>) => {
        // console.log(response);
        this.reportPeriods = (response ?? []).map(period => ({
          ...period,
          ...(getLocalReportingPeriodDates(period.key) ?? {})
        }));

        this.refreshReportPeriodTitles();

        this.onReportPeriodChange("Past Week");
      });
  }

  get selectedReportPeriod(): string | undefined | null { return this.reportForm?.controls?.key?.value; }
  get datesReadonly(): boolean { return !(this.selectedReportPeriod == 'Custom'); }
  onReportPeriodChange(selectedPeriod: string) {
    let period = this.reportPeriods.find(f => f.key == selectedPeriod);

    // console.log(period);

    this.DateStart = period?.startDate as string;
    this.DateStop = period?.stopDate as string;

    this.reportForm = this.fb.group({
      key: [selectedPeriod, [Validators.required]],
      startDate: [this.DateStart, [Validators.required]],
      stopDate: [this.DateStop, [Validators.required]],
    });
  }

  private refreshReportPeriodTitles(): void {
    this.reportPeriodOptions = (this.reportPeriods ?? [])
      .filter((p): p is IReportingPeriod & { key: string } => typeof p.key === 'string' && p.key.trim().length > 0)
      .map(p => {
      const code = this.toReportingPeriodCode(p.key);
      const displayKey = code ? `REPORTING_PERIOD.${code}` : null;
      const translatedTitle = displayKey ? this.translate.instant(displayKey) : '';

      return {
        key: p.key,
        title: translatedTitle && translatedTitle !== displayKey ? translatedTitle : p.key,
      };
    });
  }

  private toReportingPeriodCode(periodKey: string | null | undefined): string | null {
    if (!periodKey) return null;

    const code = periodKey
      .trim()
      .replace(/[^a-zA-Z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .toUpperCase();

    return code || null;
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}

interface IReportingPeriodOption {
  key: string;
  title: string;
}